import { Button, Flex, Modal, type TableColumnsType, theme, Typography } from 'antd';
import { type SortOrder } from 'antd/es/table/interface';
import { useQuery } from 'jsonapi-react';
import { Check, CircleAlert, PlusCircle, Trash2 } from 'lucide-react';
import PropTypes from 'prop-types';
import { type Key, type ReactNode, useReducer, useState } from 'react';
import { from, tap } from 'rxjs';
import BeverageForm from './beverage.form';
import BeverageFilter from './beverages.filter';
import AuthorizedComponent from '../../../../components/authorized-component';
import { DataTable } from '../../../../components/data-table';
import SyncSuspense from '../../../../components/sync-suspense';
import { ExtraContent } from '../../../../contexts/extra-content-context-provider';
import { Beverage } from '../../../../entities';
import useDebouncedState from '../../../../hooks/use-debounced-state';
import { useNotification } from '../../../../hooks/use-notification';
import { Roles } from '../../../../types';
import utils from '../../../../utils/utils';

export interface BeverageFiltersProps {
  onChange?: (filters: FiltersState) => void;
  initialFilters: FiltersState;
}

interface FiltersState {
  query: string;
}

interface BeveragePageState {
  filters: BeverageFiltersProps['initialFilters'];
  pagination: { current: number; pageSize: number };
  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_SORT_FIELD = 'name';
const DEFAULT_SORT_DIRECTION: SortOrder = 'ascend';
const SUCCESS_DELETE_MESSAGE = 'Beverage deleted successfully';

const serializeSort = (sort: BeveragePageState['sort']): string | undefined => {
  if (sort.field && sort.order) {
    const field = Array.isArray(sort.field) ? sort.field.join(',') : sort.field.toString();
    return `${sort.order === 'descend' ? '-' : ''}${field}`;
  }
  return undefined;
};

const serializeFilters = (filters: BeveragePageState['filters']): string | undefined => {
  const parts: string[] = [];
  if (filters?.query) parts.push(`name=ini='*${filters.query}*'`);

  return parts.length ? parts.join(';') : undefined;
};

const reducer = (
  state: BeveragePageState,
  action: { type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT'; payload?: any }
): BeveragePageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: { query: '' } };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload,
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload,
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as BeveragePageState['sort'],
      };
  }
};
const BeveragesPage = (): ReactNode => {
  const [editedBeverage, setEditedBeverage] = useState<Beverage | undefined>(undefined);
  const { token } = theme.useToken();
  const notification = useNotification();
  const [state, setState] = useReducer(reducer, {
    filters: { query: '' },
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const [debouncedState] = useDebouncedState({ state, time: 1000 });
  const beverages = useQuery<Beverage[]>(
    debouncedState && [
      'beverage',
      {
        filter: serializeFilters(debouncedState.filters),
        page: {
          size: debouncedState.pagination.pageSize,
          number: debouncedState.pagination.current,
          totals: 1,
        },
        sort: serializeSort(debouncedState.sort),
      },
    ]
  );

  const columns: TableColumnsType<Beverage> = [
    {
      title: 'Name',
      dataIndex: ['name'],
      sorter: true,
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            setEditedBeverage(item);
          }}
        >
          {value}
        </Typography.Link>
      ),
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
    {
      title: 'Description',
      dataIndex: ['description'],
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Price',
      dataIndex: ['price'],
      render: (_, record) => utils.currencyFormat(record.price),
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Extra',
      dataIndex: ['isExtra'],
      render: (_, record) => <>{record.isExtra ? <Check /> : undefined}</>,
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
  ];

  const handleTableChange = (pagination: any, f: any, sorter: any): void => {
    setState({
      type: 'SET_PAGINATION',
      payload: { current: pagination.page, pageSize: pagination.size },
    });
    setState({ type: 'SET_SORT', payload: Array.isArray(sorter) ? sorter[0] : sorter });
  };
  return (
    <Flex gap="middle" vertical>
      <DataTable<Beverage>
        title="Beverages"
        description="List of beverages customers can order"
        filtersNode={
          <BeverageFilter
            initialFilters={state.filters}
            onChange={(filters) => {
              setState({ type: 'SET_FILTER', payload: filters });
            }}
          />
        }
        actionsNode={
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <Button
              type="primary"
              icon={<PlusCircle />}
              onClick={() => {
                setEditedBeverage(new Beverage());
              }}
            >
              Add Beverage
            </Button>
          </AuthorizedComponent>
        }
        dataList={beverages}
        currentPage={state.pagination.current}
        onChange={setState}
        columns={columns}
        actions={[
          {
            key: 'delete',
            label: 'Delete',
            icon: <Trash2 />,
            danger: true,
            onItemClick: (record) => {
              Modal.confirm({
                width: '50%',
                title: 'Confirm deletion',
                icon: <CircleAlert />,
                content: (
                  <Typography.Text>
                    Are you sure you want to delete <b>{record.name}</b> permanently?
                  </Typography.Text>
                ),
                okText: 'Yes',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => {
                  from(beverages.client.delete(['beverage', record.id]))
                    .pipe(
                      tap((res) => {
                        if (res?.error) {
                          notification.error({
                            message: `Failed to delete ${record.name}`,
                            description: res.error.detail,
                          });
                        } else {
                          notification.success({
                            message: ' ',
                            description: SUCCESS_DELETE_MESSAGE,
                          });
                          beverages.refetch?.();
                        }
                      })
                    )
                    .subscribe();
                },
              });
            },
          },
        ]}
      />
      <SyncSuspense condition={editedBeverage}>
        <ExtraContent>
          <BeverageForm
            beverage={editedBeverage ?? new Beverage()}
            onSuccess={() => {
              beverages.refetch?.();
              setEditedBeverage(undefined);
            }}
            onCancel={() => {
              setEditedBeverage(undefined);
            }}
          />
        </ExtraContent>
      </SyncSuspense>
    </Flex>
  );
};

BeveragesPage.propTypes = {
  defaultPageNumber: PropTypes.number,
  defaultPageSize: PropTypes.number,
};

BeveragesPage.defaultProps = {
  defaultPageNumber: 1,
  defaultPageSize: 10,
  onVisibleColumnsChange: () => {},
  onSelectRowsChange: () => {},
};

BeveragesPage.displayName = 'BeveragesPage';
export default BeveragesPage;
